import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import SEO from '../../components/SEO';
import Logo from '../../components/Logo';

// Module visuals and headline stats. Feature copy comes from
// landing.moduleDetails.modules.<id>.features and stat labels from
// landing.moduleDetails.stats.* (both in the locale files).
const moduleData = {
  sales: {
    icon: '💰',
    color: 'from-orange-500 to-red-500',
    image: 'https://images.unsplash.com/photo-1650327034581-1711a15a5430?w=1200&q=80',
    stats: [
      { labelKey: 'landing.moduleDetails.stats.feature', value: '12+' },
      { labelKey: 'landing.moduleDetails.stats.report', value: '8' },
      { labelKey: 'landing.moduleDetails.stats.integration', value: '5' }
    ]
  },
  inventory: {
    icon: '📦',
    color: 'from-green-500 to-teal-500',
    image: 'https://images.pexels.com/photos/31112250/pexels-photo-31112250.jpeg?w=1200&q=80',
    stats: [
      { labelKey: 'landing.moduleDetails.stats.feature', value: '28' },
      { labelKey: 'landing.moduleDetails.stats.warehouse', valueKey: 'landing.moduleDetails.stats.unlimited' },
      { labelKey: 'landing.moduleDetails.stats.valuationMethod', value: '3' }
    ]
  },
  service: {
    icon: '🔧',
    color: 'from-blue-500 to-cyan-500',
    image: 'https://images.unsplash.com/photo-1580982324076-d95230549339?w=1200&q=80',
    stats: [
      { labelKey: 'landing.moduleDetails.stats.feature', value: '12' },
      { labelKey: 'landing.moduleDetails.stats.slaType', value: '4' },
      { labelKey: 'landing.moduleDetails.stats.report', value: '6' }
    ]
  },
  hr: {
    icon: '👥',
    color: 'from-purple-500 to-pink-500',
    image: 'https://images.pexels.com/photos/3733584/pexels-photo-3733584.jpeg?w=1200&q=80',
    stats: [
      { labelKey: 'landing.moduleDetails.stats.feature', value: '9' },
      { labelKey: 'landing.moduleDetails.stats.leaveType', valueKey: 'landing.moduleDetails.stats.unlimited' },
      { labelKey: 'landing.moduleDetails.stats.approvalLevel', value: '5' }
    ]
  },
  purchasing: {
    icon: '🛒',
    color: 'from-indigo-500 to-blue-500',
    image: 'https://images.unsplash.com/photo-1560250163-17506787d971?w=1200&q=80',
    stats: [
      { labelKey: 'landing.moduleDetails.stats.feature', value: '6' },
      { labelKey: 'landing.moduleDetails.stats.supplier', valueKey: 'landing.moduleDetails.stats.unlimited' },
      { labelKey: 'landing.moduleDetails.stats.approvalFlow', value: '3' }
    ]
  },
  tender: {
    icon: '⚖️',
    color: 'from-yellow-500 to-orange-500',
    image: 'https://images.unsplash.com/photo-1648822145238-16e13a1591da?w=1200&q=80',
    stats: [
      { labelKey: 'landing.moduleDetails.stats.feature', value: '8' },
      { labelKey: 'landing.moduleDetails.stats.auction', valueKey: 'landing.moduleDetails.stats.live' },
      { labelKey: 'landing.moduleDetails.stats.supplierPortal', valueKey: 'landing.moduleDetails.stats.available' }
    ]
  },
  accounting: {
    icon: '📊',
    color: 'from-red-500 to-pink-500',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=80',
    stats: [
      { labelKey: 'landing.moduleDetails.stats.feature', value: '14' },
      { labelKey: 'landing.moduleDetails.stats.eDocument', value: '3' },
      { labelKey: 'landing.moduleDetails.stats.report', value: '10+' }
    ]
  },
  finance: {
    icon: '💳',
    color: 'from-emerald-500 to-teal-500',
    image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=1200&q=80',
    stats: [
      { labelKey: 'landing.moduleDetails.stats.feature', value: '14' },
      { labelKey: 'landing.moduleDetails.stats.currency', value: '50+' },
      { labelKey: 'landing.moduleDetails.stats.report', value: '8' }
    ]
  },
  crm: {
    icon: '🤝',
    color: 'from-cyan-500 to-blue-500',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&q=80',
    stats: [
      { labelKey: 'landing.moduleDetails.stats.feature', value: '10' },
      { labelKey: 'landing.moduleDetails.stats.pipelineStage', value: '7' },
      { labelKey: 'landing.moduleDetails.stats.report', value: '6' }
    ]
  },
  tasks: {
    icon: '✅',
    color: 'from-violet-500 to-purple-500',
    image: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=1200&q=80',
    stats: [
      { labelKey: 'landing.moduleDetails.stats.feature', value: '6' },
      { labelKey: 'landing.moduleDetails.stats.column', valueKey: 'landing.moduleDetails.stats.customizable' },
      { labelKey: 'landing.moduleDetails.stats.view', value: 'Kanban' }
    ]
  },
  production: {
    icon: '🏭',
    color: 'from-sky-500 to-blue-600',
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&q=80',
    stats: [
      { labelKey: 'landing.moduleDetails.stats.feature', value: '13' },
      { labelKey: 'landing.moduleDetails.stats.report', value: '8' },
      { labelKey: 'landing.moduleDetails.stats.metric', value: 'OEE' }
    ]
  },
  assets: {
    icon: '💼',
    color: 'from-gray-500 to-slate-600',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80',
    stats: [
      { labelKey: 'landing.moduleDetails.stats.feature', value: '6' },
      { labelKey: 'landing.moduleDetails.stats.category', valueKey: 'landing.moduleDetails.stats.unlimited' },
      { labelKey: 'landing.moduleDetails.stats.tracking', valueKey: 'moduleInfo.detail.statValues.qrBarcode' }
    ]
  },
  office: {
    icon: '🏢',
    color: 'from-indigo-500 to-purple-500',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80',
    stats: [
      { labelKey: 'landing.moduleDetails.stats.feature', value: '7' },
      { labelKey: 'landing.moduleDetails.stats.status', valueKey: 'landing.moduleDetails.stats.preparing' },
      { labelKey: 'landing.moduleDetails.stats.module', valueKey: 'landing.moduleDetails.stats.comingSoon' }
    ]
  },
  // Vertical modules: their copy comes from moduleInfo.modules.<id> (tagline +
  // feature list, the same text /modules-info shows), see getModuleFeatures.
  reservations: {
    icon: '🏨',
    color: 'from-sky-500 to-blue-600',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80',
    stats: []
  },
  insurance: {
    icon: '🛡️',
    color: 'from-emerald-500 to-teal-600',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=80',
    stats: []
  },
  education: {
    icon: '🎓',
    color: 'from-amber-500 to-orange-600',
    image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200&q=80',
    stats: []
  },
  membership: {
    icon: '🏋️',
    color: 'from-rose-500 to-pink-600',
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200&q=80',
    stats: []
  },
  ledger: {
    icon: '📒',
    color: 'from-slate-600 to-blue-700',
    image: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1200&q=80',
    stats: []
  }
};

const ModuleDetailPage = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const module = moduleData[moduleId];

  // Scroll to the top when the page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [moduleId]);

  // Feature cards come from the locale files: landing.moduleDetails (icon,
  // title, desc) for the core modules, else the moduleInfo feature list.
  const getModuleFeatures = () => {
    const translatedFeatures = t(`landing.moduleDetails.modules.${moduleId}.features`, { returnObjects: true });
    if (Array.isArray(translatedFeatures)) return translatedFeatures;
    const infoFeatures = t(`moduleInfo.modules.${moduleId}.features`, { returnObjects: true });
    return Array.isArray(infoFeatures)
      ? infoFeatures.map((title) => ({ icon: module?.icon, title, desc: '' }))
      : [];
  };

  if (!module) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-surface-50 mb-4">{t('landing.moduleDetails.notFound')}</h1>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            {t('landing.moduleDetails.backToHome')}
          </button>
        </div>
      </div>
    );
  }

  const moduleTitle = t(`shared.moduleNames.${moduleId}`, moduleId.charAt(0).toUpperCase() + moduleId.slice(1));
  const moduleDesc = t(`landing.features.modules.${moduleId}.description`, '')
    || t(`moduleInfo.modules.${moduleId}.tagline`, '');
  // {{code}} in the SEO copy reads as a word ("... süreçlerini"), not the id.
  const moduleCode = moduleTitle.toLocaleLowerCase(i18n.resolvedLanguage || 'tr');
  const stats = module.stats.length
    ? module.stats
    : [{ labelKey: 'landing.moduleDetails.stats.feature', value: String(getModuleFeatures().length) }];

  return (
    <>
      <SEO
        title={t('moduleInfo.detail.seoTitle', { module: moduleTitle })}
        description={t('moduleInfo.detail.seoDescription', {
          description: moduleDesc,
          module: moduleTitle,
          code: moduleCode,
          featureCount: stats[0]?.value || '',
        })}
        keywords={t('moduleInfo.detail.seoKeywords', { module: moduleTitle, code: moduleCode })}
        canonical={`https://www.runvya.com/module-details/${moduleId}`}
      />

      <div className="min-h-screen bg-gray-50 dark:bg-surface-900">
        {/* Navigation */}
        <nav className="bg-white dark:bg-surface-900 shadow-md fixed w-full top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center">
                <Logo />
              </Link>
              <div className="hidden md:flex space-x-8">
                <Link to="/#features" className="text-gray-700 dark:text-surface-200 hover:text-blue-600 font-medium">
                  {t('landing.nav.modules')}
                </Link>
                <Link to="/#pricing" className="text-gray-700 dark:text-surface-200 hover:text-blue-600 font-medium">
                  {t('landing.nav.pricing')}
                </Link>
                <Link to="/#contact" className="text-gray-700 dark:text-surface-200 hover:text-blue-600 font-medium">
                  {t('landing.nav.contact')}
                </Link>
              </div>
              <div className="flex items-center space-x-4">
                <LanguageSwitcher variant="landing" />
                <button
                  onClick={() => navigate('/login')}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700"
                >
                  {t('landing.nav.login')}
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <div className="pt-16">
          <div className="relative h-80 overflow-hidden">
            <img
              src={module.image}
              alt={t('moduleInfo.detail.heroImageAlt', { module: moduleTitle })}
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className={`absolute inset-0 bg-gradient-to-br ${module.color} opacity-90`}></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <div className="text-7xl mb-4">{module.icon}</div>
                <h1 className="text-4xl md:text-5xl font-bold mb-4">{moduleTitle}</h1>
                <p className="text-xl opacity-90 max-w-2xl mx-auto px-4">{moduleDesc}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="bg-white dark:bg-surface-900 shadow-md">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-bold text-blue-600">{stat.valueKey ? t(stat.valueKey) : stat.value}</div>
                  <div className="text-gray-600 dark:text-surface-400">{t(stat.labelKey)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-surface-50 mb-12">
            {t('landing.moduleDetails.allFeatures')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {getModuleFeatures().map((feature, index) => (
              <div
                key={index}
                className="bg-white dark:bg-surface-900 rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-surface-50 mb-2">{feature.title}</h3>
                <p className="text-gray-600 dark:text-surface-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className={`bg-gradient-to-br ${module.color} py-16`}>
          <div className="max-w-4xl mx-auto px-4 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">{t('landing.moduleDetails.getStarted')}</h2>
            <p className="text-xl opacity-90 mb-8">
              {t('moduleInfo.detail.tryFreeDesc', { module: moduleTitle })}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => navigate('/login')}
                className="bg-white dark:bg-surface-900 text-gray-900 dark:text-surface-50 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                {t('landing.moduleDetails.tryFree')}
              </button>
              <Link
                to="/#contact"
                className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white dark:bg-surface-900 hover:text-gray-900 dark:text-surface-50 transition"
              >
                {t('landing.moduleDetails.requestDemo')}
              </Link>
            </div>
          </div>
        </div>

        {/* Other Modules */}
        <div className="bg-gray-100 dark:bg-surface-800 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-surface-50 mb-12">
              {t('landing.moduleDetails.otherModules')}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(moduleData)
                .filter(([key]) => key !== moduleId)
                .slice(0, 6)
                .map(([key, mod]) => (
                  <Link
                    key={key}
                    to={`/module-details/${key}`}
                    className="bg-white dark:bg-surface-900 rounded-xl p-4 text-center shadow hover:shadow-lg transition group"
                  >
                    <div className="text-4xl mb-2 group-hover:scale-110 transition-transform">
                      {mod.icon}
                    </div>
                    <div className="text-sm font-medium text-gray-700 dark:text-surface-200">
                      {t(`shared.moduleNames.${key}`, key)}
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center mb-4 md:mb-0">
                <Logo onDark />
                <span className="ml-2 text-gray-400">© 2025 Runvya ERP</span>
              </div>
              <div className="flex space-x-6">
                <Link to="/" className="text-gray-400 hover:text-white">{t('landing.moduleDetails.footerLinks.home')}</Link>
                <Link to="/#features" className="text-gray-400 hover:text-white">{t('landing.moduleDetails.footerLinks.modules')}</Link>
                <Link to="/#pricing" className="text-gray-400 hover:text-white">{t('landing.moduleDetails.footerLinks.pricing')}</Link>
                <Link to="/#contact" className="text-gray-400 hover:text-white">{t('landing.moduleDetails.footerLinks.contact')}</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ModuleDetailPage;
